import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Typography, Button, IconButton } from "@material-tailwind/react";
import ReactSelect from 'react-select';
import { FaChevronLeft, FaChevronRight, FaBook, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';

const OwnerGLPosting = () => {
    const { token, user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [owners, setOwners] = useState([]);
    const [transTypes, setTransTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [postingStatus, setPostingStatus] = useState('unposted'); // 'unposted' or 'posted'

    const [filters, setFilters] = useState({
        owner_code: '',
        trans_type: '',
        week_key: '',
        date_from: '',
        date_to: ''
    });

    const weekOptions = React.useMemo(() => {
        const getISOWeek = (d) => {
            const date = new Date(d);
            date.setHours(0, 0, 0, 0);
            date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
            const week1 = new Date(date.getFullYear(), 0, 4);
            return 1 + Math.round(((date - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
        };
        const opts = [];
        const now = new Date();
        for (let i = 0; i < 30; i++) {
            const d = new Date(now);
            d.setDate(d.getDate() - i * 7);
            const wk = String(getISOWeek(d)).padStart(2, '0');
            const yr = d.getFullYear();
            const key = `${yr}_${wk}`;
            if (!opts.find(o => o.value === key)) {
                opts.push({ value: key, label: `${yr} Wk ${wk}` });
            }
        }
        return opts;
    }, []);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [showGLPostModal, setShowGLPostModal] = useState(false);
    const [selectedTransactionForGL, setSelectedTransactionForGL] = useState(null);
    const [glPostingMessage, setGLPostingMessage] = useState({ type: '', text: '' });
    const [postingLoader, setPostingLoader] = useState(false);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkPosting, setBulkPosting] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    // Format date to "Jan 12, 2026"
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch all transactions and filter client-side for gl_posted status 
            // to ensure we get a clean list specifically for reconciliation
            let query = `${rootUrl}/api/owners/transactions?limit=10000`;
            if (filters.owner_code) query += `&owner_code=${filters.owner_code}`;
            if (filters.trans_type) query += `&trans_type=${filters.trans_type}`;
            if (filters.week_key) query += `&week_key=${filters.week_key}`;
            if (filters.date_from) query += `&date_from=${filters.date_from}`;
            if (filters.date_to) query += `&date_to=${filters.date_to}`;

            const res = await axios.get(query, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                const allTrans = res.data.data;
                const filtered = allTrans.filter(t => 
                    postingStatus === 'posted' ? t.gl_posted === true : t.gl_posted === false
                );
                setTransactions(filtered);
            }
        } catch (error) {
            console.error('Error fetching transactions for GL posting:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, postingStatus, token]);

    useEffect(() => {
        const fetchOwners = async () => {
            try {
                const res = await axios.get(`${rootUrl}/owners`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOwners(res.data || []);
            } catch (error) {
                console.error('Error fetching owners:', error);
            }
        };

        const fetchTransTypes = async () => {
            try {
                const res = await axios.get(`${rootUrl}/payments/owner-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setTransTypes(res.data.data || []);
            } catch (error) {
                console.error('Error fetching transaction types:', error);
            }
        };

        fetchOwners();
        fetchTransTypes();
        fetchTransactions();
    }, [fetchTransactions, token]);

    const getGLAccounts = (transType) => {
        const t = transTypes.find(x => x.type === transType);
        return { debit: t?.debit_account_code || '—', credit: t?.credit_account_code || '—' };
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
        setSelectedIds(new Set());
    };

    const allPageIds = transactions
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        .map(t => t.id);
    const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selectedIds.has(id));

    const toggleSelectAll = () => {
        if (allPageSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                allPageIds.forEach(id => next.delete(id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                allPageIds.forEach(id => next.add(id));
                return next;
            });
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const postBulkToGL = async () => {
        try {
            setBulkPosting(true);
            setBulkResult(null);
            const res = await axios.post(
                `${rootUrl}/api/gl/post-batch`,
                { transactionIds: Array.from(selectedIds) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setBulkResult(res.data);
            if (res.data.success) {
                setSelectedIds(new Set());
                setTimeout(() => {
                    setShowBulkModal(false);
                    setBulkResult(null);
                    fetchTransactions();
                }, 1800);
            }
        } catch (error) {
            setBulkResult({ success: false, message: error.response?.data?.message || 'Server error during bulk posting' });
        } finally {
            setBulkPosting(false);
        }
    };

    const postTransactionToGL = async () => {
        if (!selectedTransactionForGL) return;

        try {
            setPostingLoader(true);
            setGLPostingMessage({ type: '', text: '' });

            const response = await axios.post(
                `${rootUrl}/api/owners/${selectedTransactionForGL.owner_code}/transactions/${selectedTransactionForGL.id}/post-gl`,
                { posted_by: user?.username },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setGLPostingMessage({ type: 'success', text: 'Transaction successfully posted to General Ledger!' });
                setTimeout(() => {
                    setShowGLPostModal(false);
                    fetchTransactions();
                    setGLPostingMessage({ type: '', text: '' });
                }, 1500);
            } else {
                setGLPostingMessage({ type: 'error', text: response.data.message || 'Posting failed' });
            }
        } catch (error) {
            setGLPostingMessage({
                type: 'error',
                text: error.response?.data?.message || 'Server error during GL posting'
            });
        } finally {
            setPostingLoader(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Owner GL Posting</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Reconcile and post owner transactions to the General Ledger
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <Card className="bg-white rounded-2xl shadow-xl p-6">
                    {/* Tab Strip */}
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => { setPostingStatus('unposted'); setSelectedIds(new Set()); }}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 flex items-center gap-2 ${postingStatus === 'unposted' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                        >
                            <FaExclamationTriangle />
                            <span className="hidden md:inline">Unposted Transactions</span>
                            <span className="md:hidden">Unposted</span>
                        </button>
                        <button
                            onClick={() => { setPostingStatus('posted'); setSelectedIds(new Set()); }}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 flex items-center gap-2 ${postingStatus === 'posted' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
                        >
                            <FaCheckCircle />
                            <span className="hidden md:inline">Posted History</span>
                            <span className="md:hidden">Posted</span>
                        </button>
                    </div>

                    <CardBody className="p-0">
                        {/* Filters Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Owner</label>
                                <ReactSelect
                                    value={filters.owner_code ? { value: filters.owner_code, label: owners.find(o => o.code === filters.owner_code)?.name || filters.owner_code } : null}
                                    onChange={(opt) => handleFilterChange('owner_code', opt?.value || '')}
                                    options={[{ value: '', label: 'All Owners' }, ...owners.map(o => ({ value: o.code, label: `${o.name} (${o.code})` }))]}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Search owner..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                                <ReactSelect
                                    value={filters.trans_type ? { value: filters.trans_type, label: transTypes.find(t => t.type === filters.trans_type)?.long_desc || filters.trans_type } : null}
                                    onChange={(opt) => handleFilterChange('trans_type', opt?.value || '')}
                                    options={[{ value: '', label: 'All Types' }, ...transTypes.map(t => ({ value: t.type, label: t.long_desc }))]}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Select type..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Week</label>
                                <ReactSelect
                                    value={filters.week_key ? { value: filters.week_key, label: weekOptions.find(w => w.value === filters.week_key)?.label || filters.week_key } : null}
                                    onChange={(opt) => handleFilterChange('week_key', opt?.value || '')}
                                    options={[{ value: '', label: 'All Weeks' }, ...weekOptions]}
                                    isClearable
                                    classNamePrefix="react-select"
                                    placeholder="Select week..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none"
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                />
                            </div>
                            <div className="flex items-end gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">To Date</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none"
                                        value={filters.date_to}
                                        onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                    />
                                </div>
                                <Button
                                    onClick={fetchTransactions}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm px-4 h-[38px] shadow-none"
                                >
                                    Filter
                                </Button>
                            </div>
                        </div>

                        {/* Bulk Action Bar */}
                        {postingStatus === 'unposted' && selectedIds.size > 0 && (
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4">
                                <span className="text-sm font-semibold text-blue-700">
                                    {selectedIds.size} transaction{selectedIds.size !== 1 ? 's' : ''} selected
                                </span>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => setSelectedIds(new Set())}
                                        className="bg-white border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-sm shadow-none px-3 py-1.5 text-xs font-bold"
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => { setBulkResult(null); setShowBulkModal(true); }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm shadow-none px-4 py-1.5 flex items-center gap-2 text-xs font-bold"
                                    >
                                        <FaBook className="w-3 h-3" />
                                        Post {selectedIds.size} to GL
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Transactions Table */}
                        <div className="overflow-x-auto rounded-xl border border-gray-100">
                            <table className="w-full min-w-max table-auto text-left border-collapse">
                                <thead className="bg-slate-50 border-b border-gray-200">
                                    <tr>
                                        {postingStatus === 'unposted' && (
                                            <th className="p-4 w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={allPageSelected}
                                                    onChange={toggleSelectAll}
                                                    className="w-4 h-4 accent-[#FF5733] cursor-pointer"
                                                    title="Select all on this page"
                                                />
                                            </th>
                                        )}
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">Date</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">Transaction ID</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">Owner</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">Type</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">GL Accounts</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase text-right">Amount</th>
                                        <th className="p-4 font-bold text-xs text-gray-600 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={postingStatus === 'unposted' ? 8 : 7} className="p-8 text-center text-gray-500">Loading records...</td></tr>
                                    ) : transactions.length === 0 ? (
                                        <tr><td colSpan={postingStatus === 'unposted' ? 8 : 7} className="p-12 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300">
                                                    <FaBook className="w-8 h-8" />
                                                </div>
                                                <Typography className="text-gray-500 font-medium">
                                                    No {postingStatus} transactions found for the selected filters
                                                </Typography>
                                            </div>
                                        </td></tr>
                                    ) : (
                                        transactions
                                            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                            .map((t) => (
                                                <tr key={t.id} className={`border-b border-gray-50 hover:bg-slate-50 transition-colors ${selectedIds.has(t.id) ? 'bg-blue-50/40' : ''}`}>
                                                    {postingStatus === 'unposted' && (
                                                        <td className="p-4 w-10">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.has(t.id)}
                                                                onChange={() => toggleSelect(t.id)}
                                                                className="w-4 h-4 accent-[#FF5733] cursor-pointer"
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="p-4 text-sm text-gray-700">{formatDate(t.date)}</td>
                                                    <td className="p-4 text-sm font-mono text-gray-600">{t.id}</td>
                                                    <td className="p-4 text-sm">
                                                        <Typography className="font-bold text-gray-800">{t.owner_name || t.owner_code}</Typography>
                                                        <Typography className="text-xs text-gray-500">{t.owner_code}</Typography>
                                                    </td>
                                                    <td className="p-4">
                                                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase">
                                                            {t.trans_type}
                                                        </span>
                                                    </td>
                                                    <td className="p-4">
                                                        {(() => {
                                                            const gl = getGLAccounts(t.trans_type);
                                                            return (
                                                                <div className="text-xs font-mono space-y-0.5">
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-red-400 font-bold w-4">Dr</span>
                                                                        <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-semibold">{gl.debit}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1">
                                                                        <span className="text-green-500 font-bold w-4">Cr</span>
                                                                        <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-semibold">{gl.credit}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="p-4 text-sm text-right font-bold text-[#2a2e30]">
                                                        {parseFloat(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-4 text-sm">
                                                        {t.gl_posted ? (
                                                            <div className="flex items-center gap-2 text-green-600 font-semibold">
                                                                <FaCheckCircle />
                                                                <span>Posted</span>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedTransactionForGL(t);
                                                                    setShowGLPostModal(true);
                                                                }}
                                                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 py-2 shadow-none flex items-center gap-2"
                                                            >
                                                                <FaBook />
                                                                Post to GL
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {transactions.length > itemsPerPage && (
                            <div className="flex justify-between items-center mt-6">
                                <Typography variant="small" className="text-gray-500">
                                    Page {currentPage} of {Math.ceil(transactions.length / itemsPerPage)}
                                </Typography>
                                <div className="flex gap-2">
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="rounded-sm border-gray-300"
                                    >
                                        <FaChevronLeft className="w-3 h-3" />
                                    </IconButton>
                                    <IconButton
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(transactions.length / itemsPerPage), p + 1))}
                                        disabled={currentPage >= Math.ceil(transactions.length / itemsPerPage)}
                                        className="rounded-sm border-gray-300"
                                    >
                                        <FaChevronRight className="w-3 h-3" />
                                    </IconButton>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>

                {/* Bulk GL Post Modal */}
                {showBulkModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                        <Card className="max-w-md w-full mx-4 shadow-2xl rounded-2xl overflow-hidden">
                            <div className="bg-[#fff8f5] p-6 border-b border-orange-100 text-center">
                                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-200">
                                    <FaBook className="w-8 h-8" />
                                </div>
                                <Typography variant="h5" className="text-[#2a2e30] font-bold">Bulk GL Posting</Typography>
                                <Typography className="text-sm text-gray-500 mt-1">
                                    Post <span className="font-bold text-blue-600">{selectedIds.size}</span> selected transaction{selectedIds.size !== 1 ? 's' : ''} to the General Ledger
                                </Typography>
                            </div>
                            <CardBody className="p-6">
                                {bulkResult && (
                                    <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${bulkResult.success ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                        {bulkResult.success ? <FaCheckCircle className="mt-0.5 shrink-0" /> : <FaExclamationTriangle className="mt-0.5 shrink-0" />}
                                        <div className="text-sm font-semibold">
                                            {bulkResult.message || (bulkResult.success ? 'All transactions posted successfully.' : 'Posting failed.')}
                                            {bulkResult.posted != null && (
                                                <div className="mt-1 font-normal text-xs">
                                                    Posted: {bulkResult.posted} &nbsp;|&nbsp; Failed: {bulkResult.failed ?? 0}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <p className="text-sm text-gray-600 mb-6">
                                    This will create debit and credit GL entries for each selected transaction. This action cannot be undone.
                                </p>
                                <div className="flex gap-3">
                                    <Button
                                        onClick={() => { setShowBulkModal(false); setBulkResult(null); }}
                                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none font-bold"
                                        disabled={bulkPosting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={postBulkToGL}
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-sm shadow-none font-bold"
                                        disabled={bulkPosting}
                                    >
                                        {bulkPosting ? 'Posting...' : `Post ${selectedIds.size} Transactions`}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                )}

                {/* GL Confirmation Modal */}
                {showGLPostModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                        <Card className="max-w-md w-full mx-4 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                            <div className="bg-[#fff8f5] p-6 border-b border-orange-100 text-center">
                                <div className="w-16 h-16 bg-[#FF5733] text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-200">
                                    <FaBook className="w-8 h-8" />
                                </div>
                                <Typography variant="h5" className="text-[#2a2e30] font-bold">Post to General Ledger</Typography>
                                <Typography className="text-sm text-gray-500 mt-1">This will create debit and credit entries for the following transaction:</Typography>
                            </div>

                            <CardBody className="p-6">
                                {glPostingMessage.text && (
                                    <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${glPostingMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                                        {glPostingMessage.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                                        <span className="text-sm font-semibold">{glPostingMessage.text}</span>
                                    </div>
                                )}

                                <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <div className="flex justify-between border-b border-gray-200 pb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase">ID</span>
                                        <span className="text-sm font-mono font-semibold">{selectedTransactionForGL?.id}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-200 pb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase">Owner</span>
                                        <span className="text-sm font-semibold">{selectedTransactionForGL?.owner_name}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-200 pb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase">Type</span>
                                        <span className="text-sm font-bold text-blue-600">{selectedTransactionForGL?.trans_type}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-xs font-bold text-gray-500 uppercase">Amount</span>
                                        <span className="text-sm font-bold text-[#FF5733]">
                                            {parseFloat(selectedTransactionForGL?.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    {selectedTransactionForGL && (() => {
                                        const gl = getGLAccounts(selectedTransactionForGL.trans_type);
                                        return (
                                            <div className="mt-3 pt-3 border-t border-gray-200">
                                                <span className="text-xs font-bold text-gray-500 uppercase block mb-2">GL Posting</span>
                                                <div className="flex flex-col gap-1 font-mono text-xs">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-red-500 font-bold w-4">Dr</span>
                                                        <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded font-semibold">{gl.debit}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-green-600 font-bold w-4">Cr</span>
                                                        <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded font-semibold">{gl.credit}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="flex gap-3 mt-8">
                                    <Button
                                        onClick={() => setShowGLPostModal(false)}
                                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none font-bold"
                                        disabled={postingLoader}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={postTransactionToGL}
                                        className="flex-1 bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm shadow-none font-bold"
                                        disabled={postingLoader}
                                    >
                                        {postingLoader ? 'Processing...' : 'Confirm Post'}
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnerGLPosting;
